'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { Spinner } from '@/app/components/common/Spinner'
import { Button } from '@/app/components/common/Button'
import { Header } from '@/app/components/common/Header'
import { logError } from '@/lib/logging'
import { env } from '@/lib/env'

interface DocuSignSigningProps {
  /** The contract ID (for refreshing signing URL) */
  contractId: string
  /** One-time DocuSign signing URL */
  signingUrl: string
  /** Called when signing completes successfully */
  onComplete: () => void
  /** Called when user wants to go back */
  onBack?: () => void
}

type SigningState = 'loading' | 'ready' | 'error' | 'declined'

/**
 * Embeds DocuSign's focused view signing experience via their JS SDK.
 * Loads the DocuSign bundle dynamically and mounts the signing session
 * in a container div.
 */
export function DocuSignSigning({
  contractId,
  signingUrl,
  onComplete,
  onBack,
}: DocuSignSigningProps) {
  const integrationKey = env('NEXT_PUBLIC_DOCUSIGN_INTEGRATION_KEY') || ''
  const containerRef = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<SigningState>('loading')
  const [currentSigningUrl, setCurrentSigningUrl] = useState(signingUrl)
  const mountedRef = useRef(false)

  const initSigning = useCallback(
    async (url: string) => {
      if (!containerRef.current || mountedRef.current) return

      try {
        // Load DocuSign JS bundle
        const docusign = await loadDocuSignBundle(integrationKey)

        const signing = docusign.signing({
          url,
          displayFormat: 'focused',
          style: {
            branding: {
              primaryButton: {
                backgroundColor: '#4a7c59',
                color: '#ffffff',
              },
            },
          },
        })

        signing.on('sessionEnd', (event: { sessionEndType: string }) => {
          mountedRef.current = false
          switch (event.sessionEndType) {
            case 'signing_complete':
              // Mark contract as signed server-side before advancing to payment
              markContractSigned()
              break
            case 'decline':
            case 'cancel':
              setState('declined')
              break
            case 'exception':
              setState('error')
              break
            case 'ttl_expired':
              // Signing URL expired, need to refresh
              refreshSigningUrl()
              break
            default:
              setState('error')
          }
        })

        signing.mount(containerRef.current)
        mountedRef.current = true
        setState('ready')
      } catch (err) {
        logError('Failed to initialize DocuSign signing', err)
        setState('error')
      }
    },
    [integrationKey, onComplete]
  )

  async function markContractSigned() {
    try {
      const res = await fetch(`/api/contracts/${contractId}/docusign-complete`, { method: 'POST' })
      if (!res.ok) {
        // Non-critical: webhook will eventually update status.
        // Still advance to payment so the user isn't stuck.
        logError('Failed to mark contract signed via client-side call', await res.text())
      }
    } catch (err) {
      logError('Failed to mark contract signed via client-side call', err)
    }
    // Advance to payment regardless — the signing did complete in DocuSign
    onComplete()
  }

  async function refreshSigningUrl() {
    setState('loading')
    try {
      const res = await fetch(`/api/contracts/${contractId}/signing-url`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to refresh signing URL')
      const data = (await res.json()) as { signingUrl: string }
      setCurrentSigningUrl(data.signingUrl)
      mountedRef.current = false
    } catch (err) {
      logError('Failed to refresh signing URL', err)
      toast.error('Failed to refresh signing session. Please try again.')
      setState('error')
    }
  }

  useEffect(() => {
    initSigning(currentSigningUrl)
  }, [currentSigningUrl, initSigning])

  return (
    <div data-component-name="DocuSignSigning" className="flex flex-col flex-1 min-h-0 relative">
      {/* Container must always be in the DOM so the ref is available for mounting */}
      <div
        ref={containerRef}
        className="flex-1 min-h-[600px] [&_iframe]:!h-full [&_iframe]:!min-h-[600px]"
        style={{
          display:
            state === 'loading' || state === 'error' || state === 'declined' ? 'none' : undefined,
        }}
      />

      {state === 'loading' && (
        <div className="flex flex-col items-center justify-center py-16">
          <Spinner size="lg" />
          <p className="text-gray-600 mt-4">Loading contract for signing...</p>
        </div>
      )}

      {state === 'error' && (
        <div className="text-center py-16">
          <Header level={3}>Something went wrong</Header>
          <p className="text-gray-600 mt-2 mb-6">
            We couldn&apos;t load the signing experience. Please try again.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="primary" onClick={refreshSigningUrl}>
              Try Again
            </Button>
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                Go Back
              </Button>
            )}
          </div>
        </div>
      )}

      {state === 'declined' && (
        <div className="text-center py-16">
          <Header level={3}>Contract Not Signed</Header>
          <p className="text-gray-600 mt-2 mb-6">
            You declined to sign the contract. You can try again or go back.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="primary" onClick={refreshSigningUrl}>
              Sign Again
            </Button>
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                Go Back
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Dynamically loads the DocuSign JS bundle and initializes it with the integration key.
 * Caches the loaded instance for subsequent calls.
 */
let docusignPromise: Promise<DocuSignInstance> | null = null

interface DocuSignInstance {
  signing: (opts: { url: string; displayFormat: string; style?: Record<string, unknown> }) => {
    on: (event: string, callback: (data: { sessionEndType: string }) => void) => void
    mount: (element: HTMLElement) => void
  }
}

function loadDocuSignBundle(integrationKey: string): Promise<DocuSignInstance> {
  if (docusignPromise) return docusignPromise

  docusignPromise = new Promise((resolve, reject) => {
    // Use demo or production bundle based on DocuSign environment config
    const isDocuSignProd = env('NEXT_PUBLIC_DOCUSIGN_ENV') === 'production'
    const bundleUrl = isDocuSignProd
      ? 'https://js.docusign.com/bundle.js'
      : 'https://js-d.docusign.com/bundle.js'

    const script = document.createElement('script')
    script.src = bundleUrl
    script.async = true
    script.onload = () => {
      const w = window as unknown as {
        DocuSign?: { loadDocuSign: (key: string) => Promise<DocuSignInstance> }
      }
      if (w.DocuSign) {
        w.DocuSign.loadDocuSign(integrationKey)
          .then(resolve)
          .catch((err) => {
            docusignPromise = null
            reject(err)
          })
      } else {
        docusignPromise = null
        reject(new Error('DocuSign JS bundle loaded but DocuSign global not found'))
      }
    }
    script.onerror = () => {
      docusignPromise = null
      reject(new Error('Failed to load DocuSign JS bundle'))
    }
    document.head.appendChild(script)
  })

  return docusignPromise
}
